import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  Download,
  Gamepad2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Zap,
} from 'lucide-react';
import api from '../../services/api';
import SafeImage from '../../components/SafeImage';
import { useTenantTheme } from '../../contexts/TenantThemeContext';
import { playerApkPath } from '../../lib/playerApk';
import { tenantSlugFromHost } from '../../utils/tenantDomains';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const resolveAssetUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  return `${API_BASE}${value.startsWith('/') ? value : `/${value}`}`;
};

const fullUrl = (path) => {
  if (!path || typeof window === 'undefined') return path || '';
  return new URL(path, window.location.origin).href;
};

export default function AndroidAppDownloadPage() {
  const navigate = useNavigate();
  const params = useParams();
  const hostTenantSlug = tenantSlugFromHost();
  const activeSlug = params.tenantSlug || hostTenantSlug || '';
  const { theme } = useTenantTheme();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(Boolean(activeSlug));

  useEffect(() => {
    let live = true;
    if (!activeSlug) {
      setLoading(false);
      return () => { live = false; };
    }
    setLoading(true);
    api.get(`/api/tenants/public/${encodeURIComponent(activeSlug)}`)
      .then((res) => {
        if (live) setTenant(res.data || null);
      })
      .catch(() => {
        if (live) setTenant(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [activeSlug]);

  const brand = useMemo(() => {
    const branding = tenant?.branding || {};
    const tenantTheme = branding.theme || {};
    const homeScreen = branding.home_screen || {};
    const brandName = (
      homeScreen.app_name ||
      tenantTheme.brand_name ||
      branding.brand_name ||
      tenant?.name ||
      theme?.brand_name ||
      theme?.display_name ||
      'PlayEMP'
    );
    const logo = resolveAssetUrl(
      homeScreen.icon_url ||
      tenantTheme.logo_url ||
      tenantTheme.intro_logo_url ||
      branding.logo_url ||
      branding.icon_url ||
      theme?.logo_url ||
      theme?.intro_logo_url ||
      ''
    );
    const accent = tenantTheme.neon || tenantTheme.btn_primary_from || theme?.neon || theme?.btn_primary_from || branding.primary_color || '#22FF88';
    const accent2 = tenantTheme.btn_primary_to || theme?.btn_primary_to || branding.secondary_color || '#FACC15';
    return { name: brandName, logo, accent, accent2 };
  }, [tenant, theme]);

  const resolvedTenantSlug = tenant?.slug || activeSlug;
  const apkPath = playerApkPath(resolvedTenantSlug);
  const apkUrl = fullUrl(apkPath);
  const routePrefix = hostTenantSlug ? '' : (params.tenantSlug ? `/${params.tenantSlug}` : '');
  const continuePath = `${routePrefix}/app`;

  const benefits = [
    {
      icon: Zap,
      title: 'Faster Android flow',
      copy: 'Open the lobby directly with a smoother app-style experience.',
    },
    {
      icon: Gamepad2,
      title: 'Full-screen player UI',
      copy: 'Wallet, games, chat, and rewards stay cleaner than normal browser tabs.',
    },
    {
      icon: Bell,
      title: 'Live updates',
      copy: 'Built for payment, chat, reward, and account alerts as the app grows.',
    },
  ];

  const steps = [
    'Tap Download APK',
    'Open the downloaded file',
    'Allow install if Android asks',
    'Login and play normally',
  ];

  const handleDownload = () => {
    if (!apkUrl) {
      toast.error('Android app download is not ready for this team yet.');
      return;
    }
    toast.success('Download starting', {
      description: 'Open the APK from downloads and approve install when Android asks.',
      duration: 3200,
    });
    // The download is initiated from an authenticated player screen. Record it
    // separately from native FCM registration so admins can target players
    // who have acquired the APK even before their first native-app launch.
    api.post('/api/pwa/install-event', {
      outcome: 'apk_downloaded',
      platform_hint: 'android',
    }).catch(() => {});
    const link = document.createElement('a');
    link.href = apkUrl;
    link.download = `${resolvedTenantSlug || 'playemp'}-android.apk`;
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => link.remove(), 250);
  };

  return (
    <div className="min-h-[100dvh] bg-[#020403] text-white">
      <div
        className="min-h-[100dvh]"
        style={{
          background: [
            'linear-gradient(180deg, rgba(14,24,42,0.92), rgba(2,4,3,0.98) 56%, #020403)',
            'linear-gradient(135deg, rgba(34,255,136,0.12), transparent 34%, rgba(59,130,246,0.10) 72%, transparent)',
          ].join(', '),
        }}
      >
        <main
          className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col px-5 pb-8 pt-6"
          style={{
            paddingTop: 'calc(1.5rem + env(safe-area-inset-top))',
            paddingRight: 'max(1.25rem, env(safe-area-inset-right))',
            paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))',
            paddingLeft: 'max(1.25rem, env(safe-area-inset-left))',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/80 transition active:scale-95"
              aria-label="Go back"
              data-testid="android-download-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <span
              className="rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.26em]"
              style={{
                color: brand.accent2,
                borderColor: 'rgba(250,204,21,0.25)',
                background: 'rgba(250,204,21,0.06)',
              }}
            >
              Android App
            </span>
          </div>

          <section className="pt-9">
            <div
              className="relative overflow-hidden rounded-[30px] border p-5"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.035))',
                borderColor: 'rgba(255,255,255,0.13)',
                boxShadow: '0 24px 70px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            >
              <div
                className="mx-auto grid h-28 w-28 place-items-center rounded-[32px] border"
                style={{
                  background: 'rgba(0,0,0,0.32)',
                  borderColor: 'rgba(255,255,255,0.14)',
                  boxShadow: `0 0 42px color-mix(in oklab, ${brand.accent} 26%, transparent)`,
                }}
              >
                {brand.logo ? (
                  <SafeImage
                    src={brand.logo}
                    alt={brand.name}
                    className="h-[88px] w-[88px] rounded-3xl object-cover"
                    loading="eager"
                    fallback={<Smartphone className="h-11 w-11 text-white/70" />}
                  />
                ) : (
                  <Smartphone className="h-11 w-11 text-white/70" />
                )}
              </div>

              <div className="mt-6 text-center">
                <p
                  className="text-[10px] font-black uppercase tracking-[0.24em]"
                  style={{ color: brand.accent }}
                >
                  Official player app
                </p>
                <h1 className="display mt-2 max-w-full break-words text-3xl font-black leading-[1.04] tracking-normal text-white [overflow-wrap:anywhere] min-[380px]:text-4xl">
                  {brand.name}
                </h1>
                <p className="mt-3 text-sm leading-6 text-white/64">
                  Download the Android app for smoother play, a cleaner lobby, and quicker access to your wallet, games, chat, and rewards.
                </p>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-2">
                {['Fast', 'Secure', 'Live'].map((label) => (
                  <div key={label} className="rounded-2xl border border-white/10 bg-black/20 px-2 py-3 text-center">
                    <CheckCircle2 className="mx-auto mb-1.5 h-4 w-4" style={{ color: brand.accent }} />
                    <span className="text-xs font-black text-white/82">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-4 grid gap-3">
            {benefits.map(({ icon: Icon, title, copy }) => (
              <div
                key={title}
                className="flex items-start gap-3 rounded-2xl border p-4"
                style={{
                  background: 'rgba(255,255,255,0.055)',
                  borderColor: 'rgba(255,255,255,0.10)',
                }}
              >
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
                  style={{
                    background: 'rgba(0,0,0,0.26)',
                    border: '1px solid rgba(255,255,255,0.11)',
                    color: brand.accent,
                  }}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-black text-white">{title}</span>
                  <span className="mt-1 block text-xs leading-5 text-white/55">{copy}</span>
                </span>
              </div>
            ))}
          </section>

          <section
            className="mt-4 rounded-3xl border p-4"
            style={{
              background: 'rgba(0,0,0,0.22)',
              borderColor: 'rgba(255,255,255,0.10)',
            }}
          >
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-white/48">
              <ShieldCheck className="h-4 w-4" style={{ color: brand.accent2 }} />
              How to install
            </p>
            <div className="mt-4 grid gap-3">
              {steps.map((step, index) => (
                <div key={step} className="flex items-center gap-3 text-sm font-bold text-white/80">
                  <span
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-black text-black"
                    style={{ background: `linear-gradient(135deg, ${brand.accent}, ${brand.accent2})` }}
                  >
                    {index + 1}
                  </span>
                  {step}
                </div>
              ))}
            </div>
          </section>

          <div className="mt-auto pt-6">
            {loading && (
              <p className="mb-3 text-center text-xs text-white/42">Loading team app details...</p>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={!apkUrl || loading}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-black text-black transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${brand.accent}, ${brand.accent2})`,
                boxShadow: `0 18px 40px color-mix(in oklab, ${brand.accent} 22%, transparent)`,
              }}
              data-testid="android-download-apk"
            >
              <Download className="h-5 w-5" strokeWidth={2.8} />
              Download APK
            </button>
            <button
              type="button"
              onClick={() => navigate(continuePath)}
              className="mt-3 h-11 w-full rounded-xl text-sm font-extrabold text-white/58 transition hover:text-white active:scale-[0.98]"
              data-testid="android-continue-browser"
            >
              Continue in browser
            </button>
            {apkUrl && (
              <p className="mt-4 truncate text-center text-[10px] font-semibold text-white/32">
                {apkUrl}
              </p>
            )}
            <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] leading-5 text-white/36">
              <Sparkles className="h-3.5 w-3.5 shrink-0" />
              Only install from this official team website.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
